<template>
  <v-container :class="containerClass" fluid>
    <v-row align="baseline">
      <v-col cols="12" md="3">
        <div class="label mb-2">Student</div>
        <v-autocomplete
          v-model="filters.student_id"
          :items="students"
          item-text="name"
          item-value="id"
          :loading="isStudentListLoading"
          :search-input.sync="searchInputStudent"
          outlined
          solo
          flat
          background-color="white"
          label="Student"
          multiple
          hide-details
          dense
          clearable
          cache-items
          @change="getListData"
          @click:clear="getListData"
        />
      </v-col>
      <v-col cols="12" md="3">
        <div class="label mb-2">Teacher</div>
        <v-autocomplete
          v-model="filters.teacher_id"
          :items="teachers"
          item-text="name"
          item-value="id"
          :loading="isTeacherListLoading"
          :search-input.sync="searchInputTeacher"
          outlined
          solo
          flat
          background-color="white"
          label="Teacher"
          multiple
          hide-details
          dense
          clearable
          @change="getListData"
          @click:clear="getListData"
        />
      </v-col>
      <v-col cols="12" md="2">
        <div class="label mb-2">Start Date</div>
        <date-picker
          v-model="filters.start_date"
          value-type="format"
          type="date"
          range
          class="mx-datepicker--dense mb-0"
          confirm
          @confirm="getListData"
          @clear="getListData"
        ></date-picker>
      </v-col>
      <v-col cols="12" md="2">
        <div class="label mb-2">End Date</div>
        <date-picker
          v-model="filters.end_date"
          value-type="format"
          type="date"
          range
          class="mx-datepicker--dense mb-0"
          confirm
          @confirm="getListData"
          @clear="getListData"
        ></date-picker>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <div class="overflow-x-auto">
          <table class="report-table bordered full-width">
            <thead>
              <tr>
                <th rowspan="2">Student</th>
                <th rowspan="2">Classroom</th>
                <th
                  v-for="term in totalTerms"
                  :key="term"
                  class="text-center"
                  :colspan="monthsPerTerm"
                >
                  Term {{ term }}
                </th>
              </tr>
              <tr>
                <th v-for="month in totalMonths" :key="month">
                  Bulan {{ parseRepeatingMonth(month) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in items" :key="i">
                <th class="text-start" style="min-width: 100px">
                  {{ item.student_name }}
                </th>
                <th class="text-start">
                  <v-btn
                    v-if="item.classroom_id === null"
                    color="primary"
                    small
                    target="blank"
                    :href="
                      $route('classrooms.create', {
                        subscription_package_id: item.package_id,
                        student_id: item.student_id,
                      })
                    "
                  >
                    Create Classroom
                  </v-btn>
                  <a
                    v-else
                    target="_blank"
                    :href="$route('classrooms.update', item.classroom_id)"
                  >
                    {{ item.classroom_name }}
                  </a>
                </th>
                <td v-for="month in totalMonths" :key="month">
                  <div
                    v-if="isSchedulesAvailable(item.schedules, month)"
                    style="min-width: 150px"
                  >
                    <v-alert
                      v-for="(schedule, j) in findSchedules(
                        item.schedules,
                        month
                      )"
                      :key="j"
                      :color="schedule.is_completed ? 'success' : 'grey'"
                      dark
                      dense
                      class="ma-1 text-start"
                    >
                      <div>
                        <a
                          :href="
                            $route('classroom-schedules.update', schedule.id)
                          "
                          class="text-decoration-none white--text text-subtitle-2"
                        >
                          {{ formatDate(schedule.class_date, 'DD MMM YYYY') }}
                        </a>
                      </div>
                      <div class="text-caption white--text">
                        {{ formatTime(schedule.start_time) }} -
                        {{ formatTime(schedule.end_time) }}
                        <br />
                        Session {{ schedule.session }}
                      </div>
                    </v-alert>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-row align="center" justify="center">
          <v-col cols="12" md="auto">
            <v-select
              v-model="options.itemsPerPage"
              :items="rowsPerPageOptions"
              outlined
              dense
              label="Rows per page"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="auto">
            <v-pagination
              v-model="options.page"
              :length="itemList.meta.last_page"
              :total-visible="10"
            ></v-pagination>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import LayoutDashboard from '@layouts/Dashboard'
import { mapGetters, mapActions } from 'vuex'
import { ListParams } from '@/models'
import utils from '@/utils'

export default {
  name: 'ClassroomMonitoring',
  layout: LayoutDashboard,
  data() {
    return {
      monthsPerTerm: 3,
      totalTerms: 50,
      options: {
        page: 1,
        itemsPerPage: 25,
      },
      filters: {
        student_id: [],
        teacher_id: [],
        start_date: null,
        end_date: null,
        class_status: 'ACTIVE',
      },
      searchInputStudent: null,
      searchInputTeacher: null,
    }
  },
  computed: {
    ...mapGetters({
      itemList: 'report/classroomMonitoring/list',
      isItemListLoading: 'report/classroomMonitoring/isListLoading',
      studentList: 'student/searchList',
      isStudentListLoading: 'student/isSearchListLoading',
      teacherList: 'teacher/list',
      isTeacherListLoading: 'teacher/isListLoading',
    }),
    totalMonths() {
      return this.totalTerms * this.monthsPerTerm
    },
    items() {
      return this.itemList.data
    },
    students() {
      return this.studentList.data
    },
    teachers() {
      return this.teacherList.data
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.getListData()
      },
    },
    searchInputStudent(val) {
      if (val) {
        this.debouncedSearchStudentList()
      }
    },
  },
  created() {
    this.init()
    this.debouncedSearchStudentList = _.debounce(this.searchStudentList, 500)
  },
  methods: {
    ...mapActions({
      getItemList: 'report/classroomMonitoring/getList',
      getStudentList: 'student/search',
      getTeacherList: 'teacher/getList',
    }),
    async init() {
      await this.getTeacherList()
      await this.getListData()
    },
    async getListData() {
      const listParams = new ListParams(this.filters, this.options)
      await this.getItemList(utils.toServerSideQuery(listParams))
    },
    parseRepeatingMonth(month) {
      return month % this.monthsPerTerm === 0
        ? this.monthsPerTerm
        : month % this.monthsPerTerm
    },
    async searchStudentList() {
      await this.getStudentList({ search: this.searchInputStudent })
    },
    isSchedulesAvailable(schedules, month) {
      const result = this.findSchedules(schedules, month)
      return result !== null && result.length > 0
    },
    findSchedules(schedules, month) {
      if (!schedules || schedules.length == 0) {
        return null
      }
      return schedules.filter((e) => {
        const searchTerm = Math.ceil(month / this.monthsPerTerm)
        const searchMonth = this.parseRepeatingMonth(month)
        return e.term === searchTerm && e.month === searchMonth
      })
    },
  },
}
</script>
<style lang="scss" scoped>
table thead tr:first-child th:first-child {
  position: sticky;
  left: 0;
  background: white;
  z-index: 2;
}

table thead tr:first-child th:nth-child(2) {
  position: sticky;
  left: 100px;
  background: white;
  z-index: 2;
}

table tbody th:first-child {
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
}

table tbody th:nth-child(2) {
  position: sticky;
  left: 100px;
  background: white;
  z-index: 1;
}
</style>
